import { ChangeEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import styled from 'styled-components';
import { Household, User } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { LocalStorageKey } from '../../data/client_local_storage';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { alert, confirm } from '../../util/confirm';
import { ModalSpinner } from '../../util/modal_spinner.tsx';
import { Button } from './button';
import { goToNextOnboardingStep, Message, TextInput } from './common';
import { Frame } from './frame';

export interface Props {
  user?: User | undefined;
  onDone?: (upsertedUser?: User) => void; // receives the created/updated member (absent on delete)
  household?: Household;
  setHousehold?: (h: Household | undefined) => Promise<void>;
  isVisible?: boolean;
  navigate?: NavigateFunction;
  embed?: boolean; // render without the full-screen Frame wrapper (e.g. inside a DialogPage)
  // Rendered inside the Frame so its height is part of the frame's --view-height budget;
  // hosts that stack a header *above* the frame push the footer buttons below the fold.
  header?: ReactElement;
}

// TODO(hewitt): Make this code work offline - we should have made changes to the local household & then synced
//               Need to allocate a temporary single character local token & assign it to the new user
//               Then the household sync needs to upsert all single character token users, stripping the
//               single character token & replacing it with the user allocated token.
//               Single character tokens may be used in the meantime for local storage - replace these prefixes.
//               Finally, it should update the household on the server once all users are upserted.

export const EditUser = ({ user, onDone, household, setHousehold, isVisible, navigate, embed, header }: Props) => {
  // Cannot use default values because this component remains mounted as long as the Settings page is visible
  const [name, setName] = useState<string>('');
  const nameRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // ref rather than state so a second tap during the same render cycle is also blocked
  const isSubmittingRef = useRef(false);
  const [activeUserToken, setActiveUserToken] = useLocalStorage(LocalStorageKey.ActiveUserToken);
  const addingNewMember = !user;
  const inOnboarding = window.location.href.includes('onboarding');

  useEffect(() => {
    // Cannot use default values because this component remains mounted as long as the Settings page is visible
    if (nameRef.current) {
      nameRef.current.value = user?.name ?? '';
    }
  }, [user, nameRef]);

  useEffect(() => {
    // isVisible is needed because this component remains mounted as long as the Settings page is visible
    if (isVisible && !user?.name) {
      nameRef.current?.focus();
    }
  }, [isVisible, inOnboarding, nameRef, user]);

  const updateHousehold = useCallback(
    async (users: User[] | undefined) => {
      if (setHousehold && users) {
        await setHousehold({ ...household, users: users } as Household);
      }
    },
    [household, setHousehold],
  );

  const submitUpsert = useCallback(async () => {
    if (!household || isSubmittingRef.current) {
      return;
    }

    if (!name || name.trim() === '') {
      void alert({
        confirmation: <span>{'Name is required'}</span>,
      });
      return;
    }

    let updatedUser = {
      ...(user ?? {}),
      name: name.trim(),
      ...(user?.email && { email: user?.email }),
    };

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      try {
        const { token: newUserToken } = await server_api.upsertUser(updatedUser);
        updatedUser = { ...updatedUser, token: newUserToken };
      } catch {
        void alert({ confirmation: <span>Network error - please try again</span> }); // bad - see comment at top of file
        return;
      }

      try {
        await updateHousehold([
          ...(household.users ?? []).filter(({ token }) => token !== updatedUser.token),
          updatedUser as User,
        ]);
      } catch {
        void alert({ confirmation: <span>Network error - please try again</span> }); // bad - see comment at top of file
        return;
      }

      if (inOnboarding && navigate) {
        setActiveUserToken(updatedUser.token);
        await goToNextOnboardingStep(navigate, { from: OnboardingPages.ChooseUser, action: 'userChosen' });
        return;
      }

      onDone?.(updatedUser as User);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [household, inOnboarding, name, navigate, onDone, setActiveUserToken, updateHousehold, user]);

  const deleteUser = useCallback(async () => {
    if (!household) {
      return;
    }

    if (user) {
      if (user.token === activeUserToken) {
        void alert({
          confirmation: <span>{'You cannot delete the active user'}</span>,
        });
        return;
      }

      if (await confirm({ confirmation: <span>{`Are you sure you want to delete ${user.name}?`}</span> })) {
        setIsDeleting(true);
        try {
          await server_api.deleteUser(user);
          await updateHousehold((household.users ?? []).filter(({ token }) => token !== user.token));
          onDone?.();
        } catch {
          void alert({ confirmation: <span>Network error - please try again</span> }); // bad - see comment at top of file
          return;
        } finally {
          setIsDeleting(false);
        }
      }
    }
  }, [activeUserToken, household, onDone, updateHousehold, user]);

  function renderButtons() {
    let addButtonLabel: string;
    if (inOnboarding) {
      addButtonLabel = 'Continue';
    } else if (addingNewMember) {
      addButtonLabel = 'Add New Member';
    } else {
      addButtonLabel = 'Done';
    }

    return (
      <>
        <ButtonWrapper $hidden={!user}>
          <Button onClick={deleteUser} isDangerous={true}>
            {'Delete Member'}
          </Button>
        </ButtonWrapper>
        <ButtonWrapper>
          <Button onClick={submitUpsert} disabled={!name || name.trim() === ''} busy={isSubmitting}>
            {addButtonLabel}
          </Button>
        </ButtonWrapper>
      </>
    );
  }

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  return (
    <>
      {isDeleting && <ModalSpinner />}
      <Frame
        onboardingPage={undefined}
        nested={true}
        mainContent={
          <>
            <Message $widthInChars={11} $lessPadding={true} $moreTopPadding={true}>
              {''}
            </Message>
            <TextInput
              type="text"
              placeholder="Your name"
              value={name}
              onChange={onChange}
              ref={nameRef}
              enterKeyHint="next"
            />
          </>
        }
        alignTop={true}
        footerContent={renderButtons()}
        onEnterKeypress={isVisible ? submitUpsert : undefined}
        containsInputElement={true}
        embed={embed}
        header={header}
      />
    </>
  );
};

const ButtonWrapper = styled.div<{
  $hidden?: boolean;
}>`
  display: ${(props) => (props.$hidden ? 'none' : 'inherit')};
  width: 100%;
  justify-content: center;
`;
